/**
 * Content model (self-authored, behaviour-compatible with the analysed app):
 *
 *   Path ──< PathUnit (a level) ──< PathUnitItem (node on the map) ──> MiniUnit ──< MiniUnitItem (step)
 *   Playground ──< Category ──< Level ──< PlaygroundItem ──> MiniUnit
 *   Drawing = the vector stroke lesson that replaces the teacher video (steps = chapters).
 *
 * Everything is plain serialisable data so the server can ship the same pack at /api/v1/content.
 */
import type { L } from '../text';
import type { ArtFigure, ArtGuide, ArtLayer, ArtStage, ArtStatus } from './art/types';

export type PathId = 'intro' | 'animals' | 'nature' | 'characters' | 'anime' | 'cuteObjects';
export const PATH_IDS: PathId[] = ['intro', 'animals', 'nature', 'characters', 'anime', 'cuteObjects'];

/** The ten skills (structure from the analysed app's skill list; names and art are ours). */
export type SkillId =
  | 'shapes'
  | 'patterns'
  | 'pressure'
  | 'shading'
  | 'texture'
  | 'fur'
  | 'lineOfAction'
  | 'expression'
  | 'creativity'
  | 'details';
export const SKILL_IDS: SkillId[] = ['shapes', 'patterns', 'pressure', 'shading', 'texture', 'fur', 'lineOfAction', 'expression', 'creativity', 'details'];

/* ---------------------------------- Drawings ---------------------------------- */

/**
 * How a stroke is rendered:
 * - line: the main pencil line
 * - guide: light construction line (dashed, faded once later steps draw over it)
 * - shade: hatching / shading (thinner, greyer)
 * - detail: thin detail line
 * - accent: coloured highlight (used sparingly, e.g. cheeks, a star)
 * - erase: (art lessons) the construction guides fade away
 * - fill: (art lessons) a region is coloured in
 */
export type StrokeTone = 'line' | 'guide' | 'shade' | 'detail' | 'accent' | 'erase' | 'fill';

export interface Stroke {
  d: string;
  tone?: StrokeTone;
  /** Line width in viewBox units (defaults per tone). */
  w?: number;
  /** Animation duration override in ms (defaults from path length). */
  ms?: number;
  /**
   * Art lessons: which figure element this stroke animates — `g<i>` guide i, `l<i>` layer i
   * (outline / ink / spot), `f<i>` colour of region i, `e` erase all guides.
   */
  ref?: string;
}

export interface DrawingStep {
  caption: L;
  strokes: Stroke[];
  /** Optional extra tip shown in the "guidance and tips" panel. */
  tip?: L;
  /** Art lessons: construction stage(s) this step covers (1 shapes … 5 colour & shade). */
  stage?: ArtStage[];
}

export type Difficulty = 1 | 2 | 3;

/**
 * Audience tags (docs/I18N.md "Content packs"): content meant for some languages / countries only, e.g. a Lunar New
 * Year lantern (`countries: ['CN', 'SG', 'VN']`) or a lesson relying on a word play (`locales: ['en']`). Absent =
 * common content for everyone. `locales` match along the fallback chain base ('zh' matches zh-Hans and zh-Hant);
 * `countries` are ISO 3166-1 alpha-2 codes (storefront / device region).
 */
export interface AudienceTags {
  locales?: string[];
  countries?: string[];
}

export interface Drawing extends AudienceTags {
  id: string;
  title: L;
  /** Subject tag used for challenges, recommendations and letters (e.g. "cat", "tree"). */
  subject: string;
  /** Broad category for playground / favourite subjects / weekly challenges. */
  category: SubjectCategory;
  viewBox: readonly [number, number];
  steps: DrawingStep[];
  skills: SkillId[];
  difficulty: Difficulty;
  minutes: number;
  /** The illustration (art lessons). Steps reference it through `Stroke.ref`. */
  art?: ArtFigure;
  /** 'final' = drawn in the new art system; 'pending' = legacy line drawing or stub awaiting art. */
  artStatus?: ArtStatus;
  /**
   * Curriculum v2 stub: `art` is BORROWED from another drawing (e.g. the remix base) so the app runs
   * before the real art lands. Always `artStatus: 'pending'`; world agents replace the stub.
   */
  placeholder?: boolean;

  /* ------------------- Curriculum v2 fields (docs/CURRICULUM-V2.md §E.4), all optional ------------------- */
  /** Activity type this drawing is taught as on the v2 path. */
  activity?: ActivityType;
  /** Scaffold override (default: derived from the v2 level, see `scaffoldForLevel`). */
  scaffold?: Scaffold;
  /** Why the scaffold differs from the level default (required by the validator when `scaffold` is set). */
  scaffoldNote?: string;
  /** Playground category override (default: the owning world's category). */
  playgroundCategory?: string;
  /** Remix card: a part / param swap of a core figure (`remixFigure(base.art, swaps)`). */
  remix?: RemixInfo;
  /** Scene / masterpiece: composed from learnt figures (`composeFigure`). */
  composition?: Composition;
  /** Then & now: the L1 core lesson redrawn with L4 skills. */
  thenNowOf?: string;
  /** Crossover: the two worlds + source drawings. */
  crossover?: { worlds: [PathId, PathId]; sources: string[] };
  /** Picture tier ("Vẽ tranh", docs/CURRICULUM-V2.md "Bổ sung 2026-10-19"): the full picture of an object lesson. */
  picture?: PictureInfo;
  /** One-line character bio (name, hook, quirk) shown on the lesson intro and the creature book card. */
  bio?: L;
}

/** One named background part of a picture (the vignette shows it ⇒ the picture lesson draws AND colours it). */
export interface PictureElement {
  /** Backdrop part name (layers `p: 'bd:<part>'` in the composed figure). */
  part: string;
  label: L;
  /** sky / ground = background regions (colour step required); scenery / prop = drawn + coloured. */
  kind: 'sky' | 'ground' | 'scenery' | 'prop';
}

/** Tier-2 picture: an object lesson recapped in a new pose + its scenery, sky / ground colouring, light and shadow. */
export interface PictureInfo {
  world: PathId;
  /** The object drawing (core lesson, remix card or playground-only item). */
  of: string;
  /** Path activity of the object (core id / remix node id); playground-only items: undefined. */
  activity?: string;
  /** Pose of the object in the picture (also used by the vignette). */
  pose: PartSwap[];
  /** Object placement in the 240 picture: centre x, y, scale (as `ScenePlacement`), mirrored. */
  place: { x: number; y: number; scale: number; flip?: boolean };
  /** Background parts in drawing order (back → front). */
  elements: PictureElement[];
  /** The picture's one light (drives the glow, the cast shadows, the captions and the light demo). */
  light?: PictureLight;
}

export type PictureLightKind = 'sun' | 'moon' | 'window' | 'lamp';
/** Light source of a picture: kind + position in the 240 picture. */
export interface PictureLight {
  kind: PictureLightKind;
  x: number;
  y: number;
}

/* ------------------------------ Curriculum v2 types ------------------------------ */

export type ActivityType = 'doodle' | 'core' | 'remix' | 'colouring' | 'scene' | 'masterpiece' | 'thenNow' | 'challenge' | 'observe' | 'crossover' | 'picture';
export const ACTIVITY_TYPES: ActivityType[] = ['doodle', 'core', 'remix', 'colouring', 'scene', 'masterpiece', 'thenNow', 'challenge', 'observe', 'crossover', 'picture'];
/** Scaffolding fade: L1 trace (ghost of the final outline) → L2 guides → L3 main shapes only → L4 none (+ "Need a hint?"). */
export type Scaffold = 'trace' | 'guides' | 'mainShapes' | 'none';
export const SCAFFOLDS: Scaffold[] = ['trace', 'guides', 'mainShapes', 'none'];
export type CelebrationVariant = 'small' | 'key' | 'masterpiece' | 'badge';
export type RemixAxis = 'expression' | 'accessory' | 'outfit' | 'job' | 'material' | 'pose' | 'age';
export type StoryGuide = 'scribble' | 'miso' | 'hatDe' | 'bolt' | 'nova' | 'banhBao';
export type StoryFinaleKind = 'flipbook' | 'concert' | 'seasonWheel' | 'parade' | 'lanterns' | 'party';

/** One swap of a remix card: remove / add parts, extra guides, simple pose moves of named parts. */
export interface PartSwap {
  remove?: string[];
  add?: ArtLayer[];
  guides?: ArtGuide[];
  pose?: { part: string; rotate?: number; about?: [number, number]; shift?: [number, number] }[];
}

export interface RemixInfo {
  /** The core drawing this card varies. */
  base: string;
  axis: RemixAxis;
  /** Card label (front of the card). */
  card: L;
  swaps: PartSwap[];
  /** Start with a quick recap chapter of the kept parts (default true). */
  recap?: boolean;
}

export interface ScenePlacement {
  drawingId: string;
  /** Centre of the placed figure in the 240 x 240 scene. */
  x: number;
  y: number;
  scale: number;
  flip?: boolean;
  /** Back (low) → front (high). */
  z: number;
  /** Draw it up to the outline or in full colour. */
  upTo?: 'line' | 'colour';
  label: L;
}

export interface Composition {
  /** Backdrop figure (drawn in the first step). */
  backdrop: ArtFigure;
  horizonY?: number;
  /** Masterpiece: the three depth bands (y ranges in scene units). */
  bands?: { back: [number, number]; mid: [number, number]; front: [number, number] };
  placements: ScenePlacement[];
  /** Masterpiece: a second layout to choose from. */
  alternates?: ScenePlacement[][];
  /** Masterpiece: slots whose subject can be swapped for another learnt drawing. */
  swappable?: { slot: number; options: string[] }[];
}

export interface StoryChapter {
  title: L;
  /** One-line chapter beat (map sign / chapter card). */
  beat: L;
  /** Guide's line when the level starts. */
  intro: L;
  /** Guide's line when the level is done. */
  outro: L;
}

export interface WorldStory {
  title: L;
  premise: L;
  guide: StoryGuide;
  finale: { kind: StoryFinaleKind; text: L };
}

export interface ChallengeDef {
  id: string;
  path: PathId;
  level: 1 | 2 | 3 | 4;
  mode: 'memory' | 'noGuides' | 'memoryNoGuides';
  peekSeconds?: number;
  /** Allowed extra peeks (the star needs peeks <= this). */
  peeks: number;
  /** Core drawings of the level the child picks from. */
  pool: string[];
  /** Per drawing: 3-5 parts to self-check (part = figure part `p`). */
  checklist?: Record<string, { part: string; label: L }[]>;
  free?: boolean;
}

export type ObserveStepKind = 'look' | 'shape' | 'measure' | 'draw' | 'detail' | 'texture' | 'shade';
export interface ObserveDef {
  id: string;
  world: PathId;
  level: 1 | 2 | 3 | 4;
  title: L;
  /** What to put in front of you. */
  object: L;
  /** Mandatory safety line (shown first, read aloud). */
  safety: L;
  steps: { kind: ObserveStepKind; prompt: L; seconds?: number }[];
  /** Juno's example drawing. */
  refDrawingId?: string;
  /** May the child photograph the object (device-only)? Never for people. */
  photoObject: boolean;
  free?: boolean;
}

export interface CrossoverDef {
  id: string;
  worlds: [PathId, PathId];
  sources: string[];
  drawingId: string;
  /** Release wave (1 = launch). */
  wave: 1 | 2;
}

export type SubjectCategory = 'animals' | 'nature' | 'characters' | 'anime' | 'cuteObjects' | 'shapes' | 'food' | 'vehicles' | 'fantasy';
export const SUBJECT_CATEGORIES: SubjectCategory[] = ['animals', 'nature', 'characters', 'anime', 'cuteObjects', 'shapes', 'food', 'vehicles', 'fantasy'];

/* -------------------------------- Mini units -------------------------------- */

export type MiniUnitItemType =
  | 'video'
  | 'canvas'
  | 'interactive'
  | 'sliderReveal'
  | 'toggleImage'
  | 'gallerySelection'
  | 'characterBuilderOverlay'
  | 'scanImage'
  | 'animatedFeedback'
  | 'uploadToJournal'
  | 'celebration'
  | 'feelingSurvey'
  | 'tips'
  | 'redirect'
  | 'animation'
  /** Derived at runtime (not authored in content): stage-5 colouring coach, see colouring.ts. */
  | 'colourCoach'
  /* Curriculum v2 (docs/CURRICULUM-V2.md §A.5) */
  | 'remixPicker'
  | 'sceneStage'
  | 'storyBeat'
  | 'memoryPeek'
  | 'selfChecklist'
  | 'observeGuide'
  | 'observeCompare'
  | 'thenNowCompare'
  | 'printSheet';
/** The nine item types added by curriculum v2 (used by the v2 pack only). */
export const V2_ITEM_TYPES: MiniUnitItemType[] = ['remixPicker', 'sceneStage', 'storyBeat', 'memoryPeek', 'selfChecklist', 'observeGuide', 'observeCompare', 'thenNowCompare', 'printSheet'];
export const MINI_UNIT_ITEM_TYPES: MiniUnitItemType[] = [
  'video',
  'canvas',
  'interactive',
  'sliderReveal',
  'toggleImage',
  'gallerySelection',
  'characterBuilderOverlay',
  'scanImage',
  'animatedFeedback',
  'uploadToJournal',
  'celebration',
  'feelingSurvey',
  'tips',
  'redirect',
  'animation',
];

export type InteractiveKind = 'duplicate' | 'scatter' | 'gravity' | 'shadow' | 'pressure' | 'symmetry' | 'arrows' | 'bodyDrag';
export const INTERACTIVE_KINDS: InteractiveKind[] = ['duplicate', 'scatter', 'gravity', 'shadow', 'pressure', 'symmetry', 'arrows', 'bodyDrag'];
export const ANIMATION_KINDS: AnimationKind[] = ['pencilWarmup', 'paperReady', 'lightSource', 'lineOfAction'];
export type FeedbackType = 'segmentation' | 'placement' | 'details' | 'colour';
export type CharacterChoiceType = 'face' | 'ears' | 'eyes' | 'brows' | 'mouth' | 'eyeDistance' | 'eyeHeight' | 'mouthHeight' | 'color';
export type AnimationKind = 'pencilWarmup' | 'paperReady' | 'lightSource' | 'lineOfAction';
export type RedirectTarget = 'playground' | 'journal' | 'studio' | 'challenges' | 'path';

interface ItemBase {
  id: string;
}

export type MiniUnitItem =
  /** Animated stroke lesson: each drawing step is a chapter; the player pauses at chapter ends. */
  | (ItemBase & {
      type: 'video';
      drawingId: string;
      fromStep?: number;
      toStep?: number;
      /** v2 scaffold shown by the player (default: classic guides). */
      scaffold?: Scaffold;
      /** v2 masterpiece: remember the chapter reached (progress.chapterResume) and resume there. */
      resumable?: boolean;
      /** v2 remix: play the card chosen in this `remixPicker` item instead of `drawingId`. */
      fromChoice?: string;
    })
  /** Still reference (cumulative strokes up to `step`) + instruction. */
  | (ItemBase & { type: 'canvas'; drawingId: string; step?: number; text: L; /** v2 remix: show the card chosen in this `remixPicker` item. */ fromChoice?: string })
  | (ItemBase & { type: 'interactive'; kind: InteractiveKind; text: L })
  | (ItemBase & { type: 'sliderReveal'; drawingId: string; text: L; fromStep: number; toStep: number })
  | (ItemBase & { type: 'toggleImage'; drawingId: string; text: L; offStep: number; onStep: number })
  | (ItemBase & { type: 'gallerySelection'; text: L; options: { id: string; label: L; drawingId: string }[] })
  | (ItemBase & { type: 'characterBuilderOverlay'; text: L; choiceTypes: CharacterChoiceType[] })
  | (ItemBase & { type: 'scanImage'; allowSkipCapture: boolean; feedbackType: FeedbackType; calibration?: boolean; /** v2 colouring: the drawing whose colour zones are compared. */ drawingId?: string })
  | (ItemBase & { type: 'animatedFeedback'; reflect: SkillId; question: L })
  | (ItemBase & { type: 'uploadToJournal'; /** v2: gallery frame for this drawing (e.g. `golden-animals`). */ frame?: string })
  | (ItemBase & { type: 'celebration'; variant?: CelebrationVariant })
  | (ItemBase & { type: 'feelingSurvey'; question: L })
  | (ItemBase & { type: 'tips'; tips: L[] })
  | (ItemBase & { type: 'redirect'; to: RedirectTarget; text: L })
  | (ItemBase & { type: 'animation'; anim: AnimationKind; text: L; /** lightSource: the picture's own light (static demo); omitted = the moving sun demo. */ light?: PictureLight })
  /** Colour & shade coach (palette, light direction, colour by zones, medium tips); derived by `withColourCoach`. */
  | (ItemBase & { type: 'colourCoach'; drawingId: string; level?: 1 | 2 | 3 | 4 })
  /* ----------------------------- Curriculum v2 items ----------------------------- */
  /** Remix: 3 face-down cards, flip and pick one (remembered in progress.choices[item id]). */
  | (ItemBase & { type: 'remixPicker'; base: string; cards: { drawingId: string; label: L }[]; text: L })
  /** Scene / masterpiece layout: mini paper in 3 bands, numbered slots, blinking horizon; `arrange` = pick a layout / swap a subject. */
  | (ItemBase & { type: 'sceneStage'; drawingId: string; arrange?: boolean; text: L })
  /** Story chapter card: the world's guide speaks one line. */
  | (ItemBase & { type: 'storyBeat'; world: PathId; chapter: number; speaker: StoryGuide; title: L; line: L; phase: 'intro' | 'outro' | 'finale' })
  /** Level challenge: look at the reference for `seconds` (peeks allowed), then it hides; `noGuides` keeps it visible. */
  | (ItemBase & { type: 'memoryPeek'; challengeId: string; drawingIds: string[]; mode: ChallengeDef['mode']; seconds: number; peeks: number; text: L })
  /** Self check list of parts (picture of each part from the figure). */
  | (ItemBase & {
      type: 'selfChecklist';
      challengeId?: string;
      drawingId: string;
      items: { part: string; label: L }[];
      /** Per pool drawing (the child picks one in `memoryPeek`, remembered in progress.choices[memoryPeek id]). */
      byDrawing?: Record<string, { part: string; label: L }[]>;
      /** The memoryPeek item whose choice selects the list. */
      fromChoice?: string;
      text: L;
    })
  /** Observation cards (draw from a real object): safety line, cards with timers + voice, optional device-only object photo. */
  | (ItemBase & { type: 'observeGuide'; observeId: string })
  /** Object photo ↔ the child's drawing side by side (optional). */
  | (ItemBase & { type: 'observeCompare'; observeId: string; text: L })
  /** Then & now: before = the child's first L1 drawing (or Juno's); after = comparison slider + share card. */
  | (ItemBase & { type: 'thenNowCompare'; sourceDrawingId: string; drawingId: string; phase: 'before' | 'after'; text: L })
  /** Printable A4 sheet: trace (ghost lines) or colour (outline) — PNG / print. */
  | (ItemBase & { type: 'printSheet'; drawingId: string; mode: 'colour' | 'trace'; text: L });
/** Item types the app derives at runtime (allowed in content, never required to be used). */
export const DERIVED_ITEM_TYPES: MiniUnitItemType[] = ['colourCoach'];

export interface MiniUnit {
  id: string;
  title: L;
  /** The drawing this lesson teaches (thumbnail, journal subject, feedback reference). */
  drawingId: string;
  /** Skill XP awarded on completion. */
  skillXp: Partial<Record<SkillId, number>>;
  items: MiniUnitItem[];
  /** What to show when no drawing was captured. */
  noDrawingFallback: 'completionAnimation';
}

/* ----------------------------------- Paths ----------------------------------- */

/** v2 adds 'finale' (the world's story ending on the map, after the last level). */
/** v2 adds 'gallery' (picture tier: the world's exhibition room, after the finale). */
export type PathUnitItemType = 'header' | 'circular' | 'container' | 'key' | 'comingSoon' | 'text' | 'finale' | 'gallery';

export interface PathUnitItem {
  id: string;
  type: PathUnitItemType;
  /** For lesson nodes (circular / container). */
  miniUnitId?: string;
  title?: L;
  /** Free to play without a subscription. */
  free?: boolean;
  /** Intro path only: which coin piece the node awards. */
  coinPiece?: 'outline' | 'pattern' | 'shading';
  /** v2: activity type of the node (map icon). */
  activity?: ActivityType;
  /** v2: 'optional' nodes never block the path (challenge / observe / crossover side branches). Default 'block'. */
  gate?: 'block' | 'optional';
  /** v2: side-branch node (challenge, observe) drawn next to the level key. */
  branch?: 'challenge' | 'observe' | 'picture';
  /** Picture tier: id of the object node (path item) this picture node hangs from. */
  pictureOf?: string;
  /** v2: the main drawing (thumbnail) of the node. */
  drawingId?: string;
}

export interface PathUnit {
  id: string;
  level: number; // 1..PATH_LEVELS
  title: L;
  items: PathUnitItem[];
  /** v2: the story chapter of this level. */
  chapter?: StoryChapter;
}

export interface PathConfig {
  id: PathId;
  title: L;
  description: L;
  /** Crayon token used for the path's accents. */
  color: 'highlight' | 'game' | 'success' | 'brand' | 'coral' | 'pink';
  /** Drawing used as the path's hero thumbnail. */
  heroDrawingId: string;
  units: PathUnit[];
  /** End-of-path popup suggestion. */
  suggestNext: PathId[];
  /** v2: the world's story (chapters live on the units). */
  story?: WorldStory;
}

/* --------------------------------- Playground --------------------------------- */

export interface PlaygroundCategory {
  id: string;
  title: L;
  icon: string;
}

export interface PlaygroundItem extends AudienceTags {
  id: string;
  categoryId: string;
  miniUnitId: string;
  drawingId: string;
  /** Level band (v1: 1..3; v2: 1..4 = path level of the activity). */
  level: 1 | 2 | 3 | 4;
  /** v2: the three cards of one remix node share this id (shown as one tile with 3 variants). */
  remixOf?: string;
  /** v2: crossover item (label "Crossover"; unlocked when both worlds are complete). */
  crossover?: [PathId, PathId];
  /** Picture tier item (category 'pictures'): the object drawing id it is the picture of. */
  picture?: string;
  tags: string[];
  season?: 'spring' | 'summer' | 'autumn' | 'winter' | 'holiday';
  free?: boolean;
}

export interface PlaygroundConfig {
  categories: PlaygroundCategory[];
  items: PlaygroundItem[];
  /** Free picks shown during the first-time stage. */
  previewItemIds: string[];
}

/* ------------------------------- Other features ------------------------------- */

export interface WeeklyChallenge {
  id: string;
  title: L;
  goal: L;
  /** Drawing categories or subjects that count. */
  qualifies: { categories?: SubjectCategory[]; subjects?: string[] };
  promotedItemIds: string[];
  rewardStickerId: string;
}

export interface Sticker {
  id: string;
  label: L;
  /** Small SVG (viewBox 0 0 64 64) inner markup, self-drawn. */
  svg: string;
}

export interface StoryScene {
  /** Scene background + props keys rendered by the client illustration kit. */
  bg: 'meadow' | 'night' | 'beach' | 'studio' | 'forest' | 'snow' | 'city' | 'space';
  props: string[];
  caption: L;
  mood: 'happy' | 'curious' | 'surprised' | 'sleepy' | 'proud';
}

export interface StoryEpisode {
  id: string;
  number: number;
  title: L;
  scenes: StoryScene[];
  /** After the episode: open this playground item (CTA) or ask a question. */
  cta: { kind: 'draw'; playgroundItemId: string; text: L } | { kind: 'question'; text: L; options: L[] };
}

export interface StudioOption {
  id: string;
  label: L;
  /** Drawing whose final strokes make up this option (placed with `transform`). */
  drawingId: string;
  /** 2x3 affine transform [a, b, c, d, e, f] placing the drawing in the 400x300 reference. */
  transform: readonly [number, number, number, number, number, number];
}

export interface StudioChoice {
  id: string;
  title: L;
  style: 'carousel' | 'grid';
  options: StudioOption[];
}

export interface StudioPhase {
  id: string;
  title: L;
  steps: L[];
}

export interface StudioFlow {
  id: string;
  title: L;
  minutes: number;
  choices: StudioChoice[];
  phases: StudioPhase[];
}

export interface SkillDef {
  id: SkillId;
  title: L;
  description: L;
  /** Crayon token for the skill badge. */
  color: 'highlight' | 'game' | 'success' | 'brand' | 'coral' | 'pink';
}

/** Skill journey (classroom + practice trail): each step asks for a photo of the child's practice. */
export interface SkillJourneyStep {
  id: string;
  /** Stage title (warm-up / practice / show it off). */
  title: L;
  /** What to practise in this step. */
  prompt: L;
  /** Reference lesson shown while practising (and in the capture framing). */
  drawingId: string;
}

export interface SkillJourney {
  id: string;
  skillId: SkillId;
  steps: SkillJourneyStep[];
}

export interface ContentPack {
  version: number;
  /** Pack-level audience (a regional pack); items inside may narrow further. Absent = common pack. */
  audience?: AudienceTags;
  drawings: Drawing[];
  miniUnits: MiniUnit[];
  paths: PathConfig[];
  playground: PlaygroundConfig;
  challenges: WeeklyChallenge[];
  stickers: Sticker[];
  stories: StoryEpisode[];
  studio: StudioFlow[];
  skills: SkillDef[];
  skillJourneys: SkillJourney[];
  /** 1 = classic path (48 lessons); 2 = curriculum v2 (174 activities + side branches). */
  curriculum?: 1 | 2;
  /** v2 only: level challenges (24), observation prompts (16) and crossovers (15). */
  levelChallenges?: ChallengeDef[];
  observe?: ObserveDef[];
  crossovers?: CrossoverDef[];
}

/* ------------------------------ Templated copy ------------------------------ */

/**
 * Teacher letters: 4 milestones x 3 variants. Placeholders: {name} {age} {subjects} {count} {teacher}.
 * Per-drawing notes keyed by skill; placeholders {name} {subject}.
 */
export interface LetterTemplates {
  milestones: [L[], L[], L[], L[]];
  notes: Record<SkillId, L[]>;
  signoffs: L[];
}

/**
 * Feedback lines picked by the on-device heuristic feedback engine.
 * Placeholders: {name} {subject}.
 */
export interface ComplimentBank {
  segmentation: L[];
  placement: L[];
  details: L[];
  /** Gentle nudges when a metric is weak (still encouraging). */
  nudges: { placementLeft: L; placementRight: L; placementSmall: L; placementLarge: L; moreDetails: L; symmetry: L };
  noDrawing: L[];
  /** One reflection question per skill ("Did you ...?"). */
  reflection: Record<SkillId, L>;
  /** Share-the-moment suggested compliments from family. */
  family: L[];
  /** Draw-together cheers. */
  together: L[];
}
